'use client';

import { createClient } from '@/lib/supabase/client';
import { Presentation } from '@/app/power-point-library/components/mockData';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    const patterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return patterns.some((p) => p.test(error.message));
  }
  return false;
}

function rowToPresentation(row: any): Presentation {
  return {
    id: row.id,
    title: row.title,
    description: row.description || '',
    summary: row.summary || '',
    sessionAims: row.session_aims || '',
    deliveryTips: row.delivery_tips || '',
    uploader: row.uploader_name || '',
    uploaderInitials: row.uploader_initials || '',
    uploadDate: row.upload_date || '',
    categories: row.categories || [],
    downloadCount: row.download_count || 0,
    fileSizeKB: row.file_size_kb || 0,
    fileUrl: row.file_url || '',
    status: row.status || 'published',
    uploaderId: row.uploader_id ?? null,
  };
}

export const presentationService = {
  async getAll(): Promise<Presentation[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('presentations')
        .select('*')
        .eq('status', 'published')
        .order('upload_date', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Fetch presentations error:', error.message);
        return [];
      }
      return (data || []).map(rowToPresentation);
    } catch (err: any) {
      console.log('presentationService.getAll error:', err.message);
      throw err;
    }
  },

  async upload(
    file: File,
    meta: { title: string; summary: string; sessionAims: string; deliveryTips: string; categories: string[]; uploaderName: string; uploaderInitials: string },
    authUser?: any
  ): Promise<Presentation> {
    const supabase = createClient();

    // Use the passed-in auth user if available (avoids a second getUser() round-trip
    // which can fail in iframe / cookie-restricted environments).
    let user = authUser ?? null;
    if (!user) {
      const { data } = await supabase.auth.getUser();
      user = data?.user ?? null;
    }
    if (!user) {
      // Last resort: try the local session
      const { data: sessionData } = await supabase.auth.getSession();
      user = sessionData?.session?.user ?? null;
    }
    if (!user) throw new Error('Not authenticated');

    // Ensure user_profiles row exists (required by presentations FK)
    await supabase
      .from('user_profiles')
      .upsert(
        {
          id: user.id,
          email: user.email ?? '',
          full_name: meta.uploaderName,
          avatar_initials: meta.uploaderInitials,
          role: (user.user_metadata?.role as string) ?? 'external',
          status: 'active',
        },
        { onConflict: 'id', ignoreDuplicates: true }
      );

    // Upload file to storage
    const filePath = `${user.id}/${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
    const { error: storageError } = await supabase.storage
      .from('presentations')
      .upload(filePath, file, { cacheControl: '3600', upsert: false });

    if (storageError) throw storageError;

    // Get signed URL (valid 10 years for practical purposes)
    const { data: signedData } = await supabase.storage
      .from('presentations')
      .createSignedUrl(filePath, 60 * 60 * 24 * 365 * 10);

    const fileUrl = signedData?.signedUrl || '';

    // Insert record
    const { data, error } = await supabase
      .from('presentations')
      .insert({
        title: meta.title,
        description: '',
        summary: meta.summary,
        session_aims: meta.sessionAims,
        delivery_tips: meta.deliveryTips,
        uploader_id: user.id,
        uploader_name: meta.uploaderName,
        uploader_initials: meta.uploaderInitials,
        upload_date: new Date().toISOString().split('T')[0],
        categories: meta.categories,
        download_count: 0,
        file_size_kb: Math.round(file.size / 1024),
        file_path: filePath,
        file_url: fileUrl,
        status: 'published',
      })
      .select()
      .single();

    if (error) {
      if (isSchemaError(error)) throw error;
      throw new Error(error.message);
    }

    return rowToPresentation(data);
  },

  async incrementDownload(id: string): Promise<void> {
    const supabase = createClient();
    try {
      await supabase.rpc('increment_download_count', { ppt_id: id });
    } catch (err: any) {
      console.log('incrementDownload error:', err.message);
    }
  },

  /**
   * Increments downloads_used on user_profiles for any role.
   * Used to track total downloads per user for the permissions page.
   */
  async incrementUserDownloadUsed(userId: string): Promise<void> {
    const supabase = createClient();
    try {
      await supabase.rpc('increment_user_download_used', { user_uuid: userId });
    } catch (err: any) {
      console.log('incrementUserDownloadUsed error:', err.message);
    }
  },

  async getDownloadUrl(filePath: string): Promise<string> {
    const supabase = createClient();
    const { data, error } = await supabase.storage
      .from('presentations')
      .createSignedUrl(filePath, 3600);
    if (error) throw error;
    return data.signedUrl;
  },

  async delete(id: string, filePath: string): Promise<void> {
    const supabase = createClient();
    // Delete storage file
    if (filePath) {
      await supabase.storage.from('presentations').remove([filePath]);
    }
    // Delete DB record
    const { error } = await supabase.from('presentations').delete().eq('id', id);
    if (error) {
      if (isSchemaError(error)) throw error;
      throw new Error(error.message);
    }
  },

  async getQuota(userId: string): Promise<{ uploadsCount: number; downloadsUsed: number; downloadsAvailable: number } | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase.rpc('get_psych_download_quota', { user_uuid: userId });
      if (error || !data || data.length === 0) return null;
      const row = data[0];
      return {
        uploadsCount: row.uploads_count,
        downloadsUsed: row.downloads_used,
        downloadsAvailable: row.downloads_available,
      };
    } catch (err: any) {
      console.log('getQuota error:', err.message);
      return null;
    }
  },

  async incrementPsychUpload(userId: string): Promise<void> {
    const supabase = createClient();
    try {
      await supabase.rpc('increment_psych_upload_count', { user_uuid: userId });
    } catch (err: any) {
      console.log('incrementPsychUpload error:', err.message);
    }
  },

  async incrementPsychDownload(userId: string): Promise<void> {
    const supabase = createClient();
    try {
      await supabase.rpc('increment_psych_download_used', { user_uuid: userId });
    } catch (err: any) {
      console.log('incrementPsychDownload error:', err.message);
    }
  },

  /**
   * Atomically checks quota and consumes one download in a single DB transaction.
   * Returns true if the download is allowed, false if quota is exhausted.
   * This is the ONLY function that should be used to gate peak_psych downloads.
   */
  async tryConsumePsychDownload(userId: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase.rpc('try_consume_psych_download', { user_uuid: userId });
      if (error) {
        console.log('tryConsumePsychDownload error:', error.message);
        return false;
      }
      return data === true;
    } catch (err: any) {
      console.log('tryConsumePsychDownload error:', err.message);
      return false;
    }
  },

  async loadLibraryTags(): Promise<{ psych: string[]; age: string[]; sport: string[] }> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('library_tags')
        .select('tag_group, tag_value')
        .order('sort_order', { ascending: true });

      if (error || !data || data.length === 0) {
        return { psych: [], age: [], sport: [] };
      }

      const psych = data.filter((r) => r.tag_group === 'psych').map((r) => r.tag_value);
      const age = data.filter((r) => r.tag_group === 'age').map((r) => r.tag_value);
      const sport = data.filter((r) => r.tag_group === 'sport').map((r) => r.tag_value);
      return { psych, age, sport };
    } catch (err: any) {
      console.log('loadLibraryTags error:', err.message);
      return { psych: [], age: [], sport: [] };
    }
  },

  async addLibraryTag(group: 'psych' | 'age' | 'sport', value: string): Promise<void> {
    const supabase = createClient();
    // Get current max sort_order for this group
    const { data: existing } = await supabase
      .from('library_tags')
      .select('sort_order')
      .eq('tag_group', group)
      .order('sort_order', { ascending: false })
      .limit(1);

    const nextOrder = existing && existing.length > 0 ? existing[0].sort_order + 1 : 1;

    const { error } = await supabase
      .from('library_tags')
      .insert({ tag_group: group, tag_value: value, sort_order: nextOrder });

    if (error) throw new Error(error.message);
  },

  async removeLibraryTag(group: 'psych' | 'age' | 'sport', value: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('library_tags')
      .delete()
      .eq('tag_group', group)
      .eq('tag_value', value);

    if (error) throw new Error(error.message);
  },
};
